import { Anchor, Badge, Box, Button, Card, Container, Group, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconChevronLeft, IconFilePlus } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import Spinner from '../../components/Spinner';
import InvoiceChangeLog from '../../components/SuperAdmin/InvoiceChangeLog';
import InvoiceDomains from '../../components/SuperAdmin/InvoiceDomains';
import InvoiceForm from '../../components/SuperAdmin/InvoiceForm';
import superAdminAccents from '../../components/SuperAdmin/superAdminAccents.module.css';
import { invoiceStatusBadge, invoiceTypeLabel, invoiceTypeOf } from '../../helpers/invoiceDisplay';
import useFetch from '../../helpers/useFetch';

const convertWarning = 'This creates a new draft invoice with the same line items. The proforma itself stays as it is.';

const SuperAdminInvoiceDetailsPage = () => {
  const fetch = useFetch();
  const navigate = useNavigate();
  const [loadError, setLoadError] = useState();
  const [invoice, setInvoice] = useState();
  const [domains, setDomains] = useState();
  const [changes, setChanges] = useState([]);
  const [convertedInvoices, setConvertedInvoices] = useState([]);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [converting, setConverting] = useState(false);
  const { id } = useParams();

  const loadInvoice = useCallback(() => {
    setInvoice(undefined);
    setReason('');
    fetch(`/api/admin/invoices/${id}`)
      .then(({ invoice, changes, convertedInvoices }) => {
        setInvoice(invoice);
        setDomains(invoice?.templateData?.domains);
        // Both are omitted alongside a null invoice.
        setChanges(changes ?? []);
        setConvertedInvoices(convertedInvoices ?? []);
      })
      .catch(setLoadError);
  }, [id, fetch]);

  useEffect(loadInvoice, [loadInvoice]);

  const save = (setLoading) => {
    setLoading(true);
    fetch(`/api/admin/invoices/${invoice.id}`, {
      method: 'PUT',
      body: JSON.stringify({ invoice, reason }),
    })
      .then(({ invoice, changes }) => {
        notifications.show({
          title: 'Invoice saved',
          message: 'Your changes have been saved successfully.',
          color: 'success',
        });
        setInvoice(invoice);
        setChanges(changes ?? []);
        setReason('');
      })
      .catch((error) => {
        notifications.show({
          title: 'Error',
          message: error.message || 'There was an error saving your changes.',
          color: 'danger',
        });
      })
      .finally(() => setLoading(false));
  };

  const convert = () =>
    fetch(`/api/admin/invoices/${invoice.id}/convert`, { method: 'POST' })
      .then(({ invoice: created }) => {
        notifications.show({
          title: 'Draft invoice created',
          message: 'The new draft was created from this proforma.',
          color: 'success',
        });
        navigate(`/superAdmin/invoices/${created.id}`);
      })
      .catch((error) => {
        notifications.show({
          title: 'Error',
          message: error.message || 'The invoice could not be created.',
          color: 'danger',
        });
      });

  if (invoice === undefined) {
    return <Spinner />;
  }

  if (invoice === null) {
    return <ErrorMessage message="Invoice does not exist!" />;
  }

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  // Only drafts are editable — the API refuses a PUT on anything else — and an
  // invoice loaded from before drafts existed has no status at all.
  const editable = !invoice.status || invoice.status === 'draft';
  const canConvert =
    invoiceTypeOf(invoice) === 'proforma' &&
    invoice.status === 'issued' &&
    !convertedInvoices.some((converted) => converted.status !== 'void');

  return (
    <Container pt="md" style={{ maxWidth: '1280px' }}>
      <ConfirmationModal
        show={confirming}
        message={convertWarning}
        close={() => setConfirming(false)}
        loading={converting}
        confirm={(done) => {
          setConverting(true);
          convert().finally(() => {
            setConverting(false);
            done();
          });
        }}
      />
      <Box className={superAdminAccents.page}>
        <Card withBorder>
          {editable ? (
            <InvoiceForm invoice={invoice} setInvoice={setInvoice} save={save} reason={reason} setReason={setReason} />
          ) : (
            <>
              <Title order={1}>
                {invoiceTypeLabel(invoice.type)} # {invoice.number}
              </Title>
              <Text c="dimmed" my="md">
                Only draft invoices can be edited. This one is {invoice.status}.
              </Text>
              <Group>
                <Button variant="default" component="a" href="#/superAdmin/invoices">
                  <IconChevronLeft size={14} /> Back
                </Button>
                {canConvert && (
                  <Button onClick={() => setConfirming(true)}>
                    <IconFilePlus size={14} /> Create invoice from this proforma
                  </Button>
                )}
              </Group>
            </>
          )}
        </Card>
        {(invoice.proformaInvoiceId || convertedInvoices.length > 0) && (
          <Card withBorder mt="md">
            <Card.Section inheritPadding py="sm">
              <Title order={4} mb="sm">
                Linked invoices
              </Title>
              {invoice.proformaInvoiceId && (
                <Anchor href={`#/superAdmin/invoices/${invoice.proformaInvoiceId}`}>Created from proforma</Anchor>
              )}
              {convertedInvoices.map((converted) => (
                <Group key={converted.id} gap="xs">
                  <Anchor href={`#/superAdmin/invoices/${converted.id}`}>
                    {converted.number ? `Invoice # ${converted.number}` : 'Draft invoice'}
                  </Anchor>
                  <Badge variant="light" color={invoiceStatusBadge(converted.status).color}>
                    {invoiceStatusBadge(converted.status).label}
                  </Badge>
                </Group>
              ))}
            </Card.Section>
          </Card>
        )}
        <InvoiceDomains domains={domains} lineItems={invoice.templateData.lineItems} />
        <InvoiceChangeLog changes={changes} />
      </Box>
    </Container>
  );
};
export default SuperAdminInvoiceDetailsPage;
